import { useState, useMemo } from "react";
import { Calculator, Droplets, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useWeight } from "@/contexts/WeightContext";
import LogToTrackerButton from "@/components/protocol/LogToTrackerButton";

const BSA_DATA = {
  infant: { label: "Infant (<1yr)", head: 18, torso: 18, back: 18, arm: 4.5, leg: 6.75, hand: 1, groin: 1 },
  child:  { label: "Child (1-14yr)", head: 12, torso: 18, back: 18, arm: 8.25, leg: 8.25, hand: 1, groin: 1 },
  adult:  { label: "Adult (>14yr)", head: 9, torso: 18, back: 18, arm: 9, leg: 9, hand: 1, groin: 1 },
};

const BODY_PARTS = [
  { id: "head", label: "Head", bilateral: false },
  { id: "torso", label: "Torso (Front)", bilateral: false },
  { id: "back", label: "Back", bilateral: false },
  { id: "arm_l", label: "Left Arm", bilateral: false, ref: "arm" },
  { id: "arm_r", label: "Right Arm", bilateral: false, ref: "arm" },
  { id: "leg_l", label: "Left Leg", bilateral: false, ref: "leg" },
  { id: "leg_r", label: "Right Leg", bilateral: false, ref: "leg" },
  { id: "hand", label: "Hands/Feet", bilateral: false },
  { id: "groin", label: "Groin", bilateral: false },
];

export default function BSACalculator() {
  const [ageGroup, setAgeGroup] = useState("adult");
  const [selected, setSelected] = useState({});
  const [isElectrical, setIsElectrical] = useState(false);
  const { weight: globalWeight, setWeight: setGlobalWeight, hasWeight } = useWeight();

  // Use global weight, allow local override that syncs back
  const weight = globalWeight;
  const setWeight = (val) => setGlobalWeight(val);

  const bsa = BSA_DATA[ageGroup];

  const toggle = (partId) => {
    setSelected((prev) => ({ ...prev, [partId]: !prev[partId] }));
  };

  const totalBSA = useMemo(() => {
    let total = 0;
    for (const part of BODY_PARTS) {
      if (selected[part.id]) {
        const ref = part.ref || part.id;
        total += bsa[ref];
      }
    }
    return Math.round(total * 10) / 10;
  }, [selected, bsa]);

  const fluidCalc = useMemo(() => {
    if (!weight || !totalBSA) return null;
    const w = parseFloat(weight);
    if (isNaN(w) || w <= 0) return null;
    const multiplier = isElectrical ? 4 : 2;
    const total24hr = multiplier * w * totalBSA;
    const first8hr = total24hr / 2;
    const hourlyFirst8 = first8hr / 8;
    return { total24hr: Math.round(total24hr), first8hr: Math.round(first8hr), hourlyFirst8: Math.round(hourlyFirst8) };
  }, [weight, totalBSA, isElectrical]);

  const reset = () => { setSelected({}); setIsElectrical(false); };

  const severityLabel = totalBSA === 0 ? null
    : totalBSA < 10 ? "Minor"
    : totalBSA < 20 ? "Moderate"
    : "Major / Critical";

  const severityColor = totalBSA === 0 ? ""
    : totalBSA < 10 ? "text-green-400"
    : totalBSA < 20 ? "text-amber-400"
    : "text-red-400";

  return (
    <Card className="border-orange-500/30" data-testid="bsa-calculator">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="flex items-center justify-between text-base font-heading">
          <span className="flex items-center gap-2">
            <Calculator className="w-4 h-4 text-orange-400" />
            BSA / Burn Calculator
          </span>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={reset} data-testid="bsa-reset">
            <RotateCcw className="w-3 h-3 mr-1" />Reset
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-4">
        {/* Age Group */}
        <div className="flex gap-2" data-testid="bsa-age-group">
          {Object.entries(BSA_DATA).map(([key, val]) => (
            <button
              key={key}
              onClick={() => setAgeGroup(key)}
              className={`flex-1 text-xs py-1.5 rounded border transition-colors ${
                ageGroup === key
                  ? "bg-orange-500/20 text-orange-300 border-orange-500/40 font-medium"
                  : "border-border hover:border-orange-500/30"
              }`}
              data-testid={`bsa-age-${key}`}
            >
              {val.label}
            </button>
          ))}
        </div>

        {/* Body Parts Grid */}
        <div className="grid grid-cols-3 gap-1.5" data-testid="bsa-body-parts">
          {BODY_PARTS.map((part) => {
            const ref = part.ref || part.id;
            const pct = bsa[ref];
            const isSelected = !!selected[part.id];
            return (
              <button
                key={part.id}
                onClick={() => toggle(part.id)}
                className={`relative p-2 rounded border text-left transition-all ${
                  isSelected
                    ? "bg-orange-500/20 border-orange-500/50 ring-1 ring-orange-500/30"
                    : "border-border hover:border-orange-500/30 hover:bg-orange-500/5"
                }`}
                data-testid={`bsa-part-${part.id}`}
              >
                <div className="text-xs font-medium leading-tight">{part.label}</div>
                <div className={`text-lg font-bold ${isSelected ? "text-orange-400" : "text-muted-foreground"}`}>{pct}%</div>
              </button>
            );
          })}
        </div>

        {/* Total BSA */}
        <div className={`text-center py-3 rounded-lg ${totalBSA > 0 ? "bg-orange-500/10 border border-orange-500/30" : "bg-muted/30 border border-border"}`} data-testid="bsa-total">
          <div className="text-3xl font-bold font-mono">
            <span className={totalBSA > 0 ? "text-orange-400" : "text-muted-foreground"}>{totalBSA}%</span>
            <span className="text-sm font-normal text-muted-foreground ml-2">TBSA</span>
          </div>
          {severityLabel && (
            <div className={`text-sm font-medium mt-1 ${severityColor}`}>{severityLabel}</div>
          )}
        </div>

        {/* Fluid Resuscitation — always visible */}
        <div className="space-y-2 pt-1 border-t border-border" data-testid="bsa-fluid-section">
            <div className="flex items-center gap-2">
              <Droplets className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-heading font-semibold">Fluid Resuscitation</span>
            </div>
            <div className="flex gap-2 items-center">
              <Input
                type="text"
                inputMode="numeric"
                placeholder="Weight (kg)"
                value={weight}
                onChange={(e) => setWeight(e.target.value.replace(/[^0-9]/g, ""))}
                className={`w-28 ${hasWeight ? 'border-green-500/50 bg-green-500/10' : ''}`}
                data-testid="bsa-weight"
              />
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={isElectrical}
                  onChange={(e) => setIsElectrical(e.target.checked)}
                  className="rounded"
                  data-testid="bsa-electrical"
                />
                Electrical Burn (4mL/kg)
              </label>
            </div>
            <div className="text-xs text-muted-foreground">
              Formula: {isElectrical ? "4" : "2"}mL x {weight || "___"}kg x {totalBSA}% TBSA
            </div>
            {fluidCalc && (
              <>
                <div className="grid grid-cols-3 gap-2 pt-1">
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded p-2 text-center">
                    <div className="text-lg font-bold text-blue-400 font-mono">{fluidCalc.total24hr}<span className="text-xs font-normal ml-0.5">mL</span></div>
                    <div className="text-[10px] text-muted-foreground leading-tight">Total 24hr</div>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded p-2 text-center">
                    <div className="text-lg font-bold text-blue-400 font-mono">{fluidCalc.first8hr}<span className="text-xs font-normal ml-0.5">mL</span></div>
                    <div className="text-[10px] text-muted-foreground leading-tight">First 8hr</div>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded p-2 text-center">
                    <div className="text-lg font-bold text-blue-400 font-mono">{fluidCalc.hourlyFirst8}<span className="text-xs font-normal ml-0.5">mL/hr</span></div>
                    <div className="text-[10px] text-muted-foreground leading-tight">Rate (first 8hr)</div>
                  </div>
                </div>
                <LogToTrackerButton
                  className="w-full h-9 mt-1"
                  testId="bsa-log"
                  entry={{
                    type: "intervention",
                    name: `Burn assessment ${totalBSA}% TBSA`,
                    dose: severityLabel || "",
                    notes: `${BSA_DATA[ageGroup].label} · ${isElectrical ? "Electrical (4mL/kg)" : "Thermal (2mL/kg)"} · Wt ${weight}kg · 24hr ${fluidCalc.total24hr}mL · 1st 8hr ${fluidCalc.first8hr}mL @ ${fluidCalc.hourlyFirst8} mL/hr`,
                  }}
                  successLabel={`Burn assessment ${totalBSA}% TBSA logged`}
                  label="Log Burn Assessment to Tracker"
                />
              </>
            )}
          </div>

        {/* Reference Table */}
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground py-1">BSA Reference Chart</summary>
          <table className="w-full mt-1 border-collapse text-xs">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-1 pr-2">Body Part</th>
                <th className="text-center py-1 px-2">Infant</th>
                <th className="text-center py-1 px-2">Child</th>
                <th className="text-center py-1 px-2">Adult</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-border/50"><td className="py-1">Head</td><td className="text-center">18%</td><td className="text-center">12%</td><td className="text-center">9%</td></tr>
              <tr className="border-b border-border/50"><td className="py-1">Torso</td><td className="text-center">18%</td><td className="text-center">18%</td><td className="text-center">18%</td></tr>
              <tr className="border-b border-border/50"><td className="py-1">Back</td><td className="text-center">18%</td><td className="text-center">18%</td><td className="text-center">18%</td></tr>
              <tr className="border-b border-border/50"><td className="py-1">Each Arm</td><td className="text-center">4.5%</td><td className="text-center">8.25%</td><td className="text-center">9%</td></tr>
              <tr className="border-b border-border/50"><td className="py-1">Each Leg</td><td className="text-center">6.75%</td><td className="text-center">8.25%</td><td className="text-center">9%</td></tr>
              <tr className="border-b border-border/50"><td className="py-1">Hands</td><td className="text-center">1%</td><td className="text-center">1%</td><td className="text-center">1%</td></tr>
              <tr><td className="py-1">Groin</td><td className="text-center">1%</td><td className="text-center">1%</td><td className="text-center">1%</td></tr>
            </tbody>
          </table>
        </details>
      </CardContent>
    </Card>
  );
}
